// src/app/core/i18n/translation.service.ts
import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../config/config.service';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly http   = inject(HttpClient);
  private readonly config = inject(ConfigService);

  private readonly _translations = signal<Record<string, unknown>>({});
  private readonly _fallback     = signal<Record<string, unknown>>({});

  /** Idioma activo — Signal de solo lectura para templates */
  readonly currentLang = signal<string>('es-MX');

  /**
   * Carga el idioma inicial y opcionalmente el idioma de fallback.
   * Llamado como segunda promesa secuencial en APP_INITIALIZER junto a ConfigService.load().
   */
  async initialize(): Promise<void> {
    const lang = this.resolveInitialLanguage();
    await this.setLanguage(lang);

    // Cargar el idioma por defecto como fallback para claves faltantes
    const defaultLang = this.config.get('i18n').defaultLanguage;
    if (lang !== defaultLang) {
      const cfg = this.config.get('i18n');
      try {
        const fallbackData = await firstValueFrom(
          this.http.get<Record<string, unknown>>(`${cfg.assetsPath}${defaultLang}.json`)
        );
        this._fallback.set(fallbackData);
      } catch {
        // El fallback es best-effort — si no carga, continuar con el idioma principal
      }
    }
  }

  /**
   * Cambia el idioma activo.
   * - Carga el JSON correspondiente
   * - Actualiza `document.documentElement.lang`
   * - Persiste en localStorage (única excepción controlada)
   */
  async setLanguage(langCode: string): Promise<void> {
    const cfg  = this.config.get('i18n');
    const url  = `${cfg.assetsPath}${langCode}.json`;
    const data = await firstValueFrom(
      this.http.get<Record<string, unknown>>(url)
    );

    this._translations.set(data);
    this.currentLang.set(langCode);

    // EXCEPCIÓN CONTROLADA: localStorage solo para preferencia de idioma.
    // El idioma es el ÚNICO dato no sensible que justifica localStorage.
    // Todo lo demás (sesión, token) va en sessionStorage.
    localStorage.setItem(cfg.storageKey, langCode);

    // Actualizar el atributo lang del documento para lectores de pantalla
    // y para que el navegador aplique la ortografía correcta.
    document.documentElement.lang = langCode;
  }

  /**
   * Obtiene una traducción por clave anidada con puntos.
   * Ejemplo: get('loans.detail.title', { name: 'Juan' })
   *
   * Los valores de `params` se escapan para prevenir XSS.
   * Si la clave no existe: intentar en el idioma de fallback, luego retornar la clave.
   */
  get(key: string, params?: Record<string, string | number>): string {
    // Intentar en el idioma activo
    let value = this.resolvePath(this._translations(), key);

    // Fallback al idioma por defecto si la clave no existe
    if (value === undefined) {
      value = this.resolvePath(this._fallback(), key);
    }

    if (value === undefined) {
      // En desarrollo: la clave faltante es visible para el desarrollador
      // En producción: LoggerService registrará la clave faltante (ver GlobalErrorHandler)
      return key;
    }

    const text = String(value);
    return params ? this.interpolate(text, params) : text;
  }

  /**
   * Resuelve el idioma inicial del usuario siguiendo el orden de prioridad:
   * 1. localStorage[storageKey] si existe y está en supportedLanguages
   * 2. navigator.language normalizado (ej: 'es' → 'es-MX')
   * 3. config.i18n.defaultLanguage como último recurso
   */
  resolveInitialLanguage(): string {
    const cfg = this.config.get('i18n');

    // 1. Preferencia guardada por el usuario
    const stored = localStorage.getItem(cfg.storageKey);
    if (stored && cfg.supportedLanguages.includes(stored)) {
      return stored;
    }

    // 2. Idioma del navegador normalizado
    const normalized = this.normalizeBrowserLang(navigator.language, cfg.supportedLanguages);
    if (normalized) {
      return normalized;
    }

    // 3. Idioma por defecto de la configuración
    return cfg.defaultLanguage;
  }

  /**
   * Normaliza el idioma del navegador al formato de supportedLanguages.
   * Ejemplos:
   *   'es'       → 'es-MX'  (primer match por prefijo 'es')
   *   'es-419'   → 'es-MX'  (match por prefijo 'es')
   *   'en'       → 'en-US'  (primer match por prefijo 'en')
   *   'en-GB'    → 'en-US'  (match por prefijo 'en', no hay en-GB)
   *   'es-MX'    → 'es-MX'  (coincidencia exacta)
   *   'fr'       → null     (no soportado)
   */
  normalizeBrowserLang(lang: string, supported: string[]): string | null {
    // Coincidencia exacta primero
    if (supported.includes(lang)) {
      return lang;
    }
    // Coincidencia por prefijo de idioma (parte antes del guion)
    const prefix = lang.split('-')[0];
    return supported.find(s => s.startsWith(prefix + '-')) ?? null;
  }

  /**
   * Interpola variables en el texto de traducción.
   * Formato: "Hola {{name}}, tienes {{count}} mensajes."
   *
   * SEGURIDAD: Los valores son escapados para prevenir XSS.
   * Los valores vienen de la API de Fineract y no deben ejecutarse como HTML.
   */
  private interpolate(text: string, params: Record<string, string | number>): string {
    return text.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
      const val = params[key];
      if (val === undefined) {
        return `{{${key}}}`;
      }
      // Escapar HTML en el valor antes de insertar
      return this.escapeHtml(String(val));
    });
  }

  /**
   * Escapa caracteres HTML para prevenir inyección de código.
   * Se aplica a todos los valores interpolados en traducciones.
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  /**
   * Resuelve una clave anidada con puntos en un objeto.
   * Ejemplo: resolvePath({a: {b: 'valor'}}, 'a.b') → 'valor'
   */
  private resolvePath(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce<unknown>((acc, key) => {
      if (acc !== null && typeof acc === 'object') {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  }
}
